import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tokenStorage } from '../../utils/tokenStorage';
import { useAuth } from '../../context/AuthContext';
import { signInToSystem } from '../../utils/api';
import LoadingScreen from '../LoadingScreen';
import './AuthCallback.css';

/**
 * OAuth Callback Handler Component
 * Handles the OAuth callback from Keycloak after user authentication
 */
export function AuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshSession } = useAuth();
    const [showSessionError, setShowSessionError] = useState(false);

    useEffect(() => {
        const handleCallback = async () => {
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');

            // Verify state
            const storedState = sessionStorage.getItem('oauth_state');
            if (state !== storedState) {
                console.error('State mismatch');
                navigate('/?error=state_mismatch');
                return;
            }

            if (error) {
                console.error('OAuth error:', error);
                navigate(`/?error=${error}`);
                return;
            }

            if (!code) {
                navigate('/?error=no_code');
                return;
            }

            try {
                // Exchange authorization code for tokens
                const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
                if (!codeVerifier) {
                    throw new Error('Code verifier not found');
                }

                const tokenResponse = await exchangeCodeForTokens(code, codeVerifier);

                // Store tokens
                const expiresAt = Math.floor(Date.now() / 1000) + (tokenResponse.expires_in || 3600);
                tokenStorage.setTokens({
                    access_token: tokenResponse.access_token,
                    refresh_token: tokenResponse.refresh_token,
                    id_token: tokenResponse.id_token,
                    expires_at: expiresAt
                });

                // Perform system login
                try {
                    const systemUserData = await signInToSystem();
                    if (systemUserData) {
                        tokenStorage.setUserProfile(systemUserData);
                    } else {
                        throw new Error('System login returned no data');
                    }
                } catch (systemLoginError) {
                    console.error('System login failed:', systemLoginError);
                    // Get idToken before clearing tokens (needed for Keycloak logout)
                    const idToken = tokenStorage.getIdToken();
                    const endSessionUrl = process.env.REACT_APP_END_SESSION_URL;
                    const redirectUri = encodeURIComponent(window.location.origin + '/');

                    // Clear tokens since sign-in failed
                    tokenStorage.clearTokens();
                    sessionStorage.removeItem('pkce_code_verifier');
                    sessionStorage.removeItem('oauth_state');

                    // Show popup message
                    setShowSessionError(true);

                    // Logout from Keycloak (similar to logout function)
                    if (idToken && endSessionUrl) {
                        // Clean the endSessionUrl to remove any trailing =, ?, or whitespace
                        const cleanEndSessionUrl = endSessionUrl.trim().replace(/[=?]+$/, '');
                        const logoutUrl = `${cleanEndSessionUrl}?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${redirectUri}`;

                        setTimeout(() => {
                            window.location.replace(logoutUrl);
                        }, 3000); // Increased delay to show popup
                        return; // Exit early, redirect will happen
                    } else {
                        // If no idToken, just redirect to home
                        setTimeout(() => {
                            window.location.replace(window.location.origin + '/');
                        }, 3000); // Increased delay to show popup
                        return;
                    }
                }

                // Clean up
                sessionStorage.removeItem('pkce_code_verifier');
                sessionStorage.removeItem('oauth_state');

                // Set a flag to indicate we're processing callback
                // This prevents App.js redirect logic from running before session is set
                sessionStorage.setItem('processing_callback', 'true');

                // Refresh session in context and wait for it to complete
                await refreshSession();

                // Wait a bit to ensure React state has updated
                await new Promise(resolve => setTimeout(resolve, 200));

                // Verify tokens are stored before redirecting
                const accessToken = tokenStorage.getAccessToken();
                if (!accessToken) {
                    throw new Error('Tokens were not stored properly');
                }

                // Clear the processing flag
                sessionStorage.removeItem('processing_callback');

                // Redirect to home or intended destination
                const redirectTo = sessionStorage.getItem('redirect_after_login') || '/';
                sessionStorage.removeItem('redirect_after_login');
                navigate(redirectTo);
            } catch (error) {
                console.error('Token exchange failed:', error);
                // Get idToken before clearing tokens (if available)
                const idToken = tokenStorage.getIdToken();
                const endSessionUrl = process.env.REACT_APP_END_SESSION_URL;
                const redirectUri = encodeURIComponent(window.location.origin + '/');

                // Ensure tokens are cleared on any error
                tokenStorage.clearTokens();
                sessionStorage.removeItem('pkce_code_verifier');
                sessionStorage.removeItem('oauth_state');
                sessionStorage.removeItem('processing_callback');

                // Logout from Keycloak if we have idToken (similar to logout function)
                if (idToken && endSessionUrl) {
                    // Clean the endSessionUrl to remove any trailing =, ?, or whitespace
                    const cleanEndSessionUrl = endSessionUrl.trim().replace(/[=?]+$/, '');
                    const logoutUrl = `${cleanEndSessionUrl}?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${redirectUri}`;
                    window.location.replace(logoutUrl);
                } else {
                    // If no idToken, just redirect to home
                    window.location.replace(window.location.origin + '/');
                }
            }
        };

        handleCallback();
    }, [searchParams, navigate, refreshSession]);

    return (
        <>
            <LoadingScreen />
            {showSessionError && (
                <div className="session-error-popup">
                    <div className="session-error-popup-content">
                        <h3>Session Unavailable</h3>
                        <p>There is already someone in the session. You will be redirected shortly.</p>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * Exchange authorization code for tokens
 * @param {string} code - Authorization code from Keycloak
 * @param {string} codeVerifier - PKCE code verifier
 * @returns {Promise<Object>} Token response
 */
async function exchangeCodeForTokens(code, codeVerifier) {
    const authIssuer = process.env.REACT_APP_AUTH_ISSUER

    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID

    const redirectUri = window.location.origin + '/auth/callback';

    const params = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: clientId,
        code_verifier: codeVerifier
    });

    const response = await fetch(`${authIssuer}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error_description || error.error || 'Token exchange failed');
    }

    return await response.json();
}
